// src/MstQueryHandler.ts
import { equal } from "@wry/equality";
import { makeObservable as makeObservable2, observable as observable2, action as action2 } from "mobx";
import {
  addDisposer,
  getEnv,
  getIdentifier as getIdentifier3,
  getRoot as getRoot2,
  getSnapshot as getSnapshot2,
  getType as getType3,
  isReferenceType as isReferenceType2,
  isStateTreeNode as isStateTreeNode3,
  protect as protect2,
  recordPatches,
  unprotect as unprotect2
} from "mobx-state-tree";

// src/merge.ts
import {
  clone,
  getIdentifier as getIdentifier2,
  getRoot,
  getSnapshot,
  getType as getType2,
  isFrozenType as isFrozenType2,
  isMapType,
  isStateTreeNode as isStateTreeNode2,
  protect,
  unprotect
} from "mobx-state-tree";

// src/QueryStore.ts
import {
  isStateTreeNode,
  isAlive,
  getType,
  isArrayType,
  getIdentifier
} from "mobx-state-tree";
import { observable, action, makeObservable } from "mobx";
var getKey = (type, id) => {
  return `${type.name}:${id}`;
};
var QueryStore = class {
  #scheduledGc = null;
  #queryClient;
  #cache = /* @__PURE__ */ new Map();
  models = /* @__PURE__ */ new Map();
  constructor(queryClient) {
    makeObservable(this, {
      setQuery: action,
      removeQuery: action,
      clear: action
    });
    this.#queryClient = queryClient;
  }
  getQueries(queryDef, matcherFn = () => true) {
    let results = [];
    const arr = this.#cache.get(queryDef.name) ?? [];
    for (let query of arr) {
      if (getType(query) === queryDef && matcherFn(query)) {
        results.push(query);
      }
    }
    return results;
  }
  setQuery(q) {
    const type = getType(q);
    let arr = this.#cache.get(type.name);
    if (!arr) {
      arr = observable.array([], { deep: false });
      this.#cache.set(type.name, arr);
    }
    arr.push(q);
  }
  removeQuery(query) {
    const type = getType(query);
    this.#cache.get(type.name)?.remove(query);
  }
  clear() {
    for (let [, obj] of this.models) {
      this.#queryClient.rootStore.__MstQueryAction(
        "delete",
        getType(obj),
        getIdentifier(obj),
        obj
      );
    }
    this.models.clear();
    this.#cache.clear();
  }
  runGc() {
    if (this.#scheduledGc) {
      return;
    }
    const seenIdentifiers = /* @__PURE__ */ new Set();
    for (let [_, arr] of this.#cache) {
      for (let query of arr) {
        if (query.isLoading) {
          this.#scheduledGc = window.setTimeout(() => {
            this.#scheduledGc = null;
            this.runGc();
          }, 1e3);
          return;
        }
        collectSeenIdentifiers(query.data, seenIdentifiers);
        collectSeenIdentifiers(query.request, seenIdentifiers);
      }
    }
    for (let [key, obj] of this.models) {
      const identifier = getIdentifier(obj);
      if (!seenIdentifiers.has(getKey(getType(obj), identifier))) {
        this.models.delete(getKey(getType(obj), getIdentifier(obj)));
        this.#queryClient.rootStore.__MstQueryAction("delete", getType(obj), key, obj);
      }
    }
  }
};
function collectSeenIdentifiers(node, seenIdentifiers) {
  if (!isStateTreeNode(node)) {
    return;
  }
  if (!isAlive(node)) {
    return;
  }
  if (!node || node instanceof Date || typeof node !== "object") {
    return;
  }
  const n = node;
  const t = getType(node);
  if (isArrayType(t)) {
    n.forEach((n2) => collectSeenIdentifiers(n2, seenIdentifiers));
    return;
  }
  const nodeIdentifier = getIdentifier(n);
  if (nodeIdentifier) {
    const identifier = getKey(t, nodeIdentifier);
    if (seenIdentifiers.has(identifier)) {
      return;
    } else {
      seenIdentifiers.add(identifier);
    }
  }
  for (const key in t.properties) {
    collectSeenIdentifiers(n[key], seenIdentifiers);
  }
}

// src/utils.ts
import {
  isUnionType,
  isModelType,
  isLateType,
  isOptionalType,
  isArrayType as isArrayType2,
  isReferenceType,
  isFrozenType
} from "mobx-state-tree";
import { isObservableArray } from "mobx";
import * as React from "react";
function getRealTypeFromObject(typeDef, data, key) {
  const modelOrBaseType = getSubType(typeDef, data[key]);
  if (modelOrBaseType && modelOrBaseType.properties && !modelOrBaseType.properties[key]) {
    return null;
  }
  const subType = modelOrBaseType.properties && !isFrozenType(modelOrBaseType) ? getSubType(modelOrBaseType.properties[key], data[key]) : modelOrBaseType;
  return subType;
}
function getSubType(t, data) {
  if (isUnionType(t)) {
    const actualType = t.determineType && data !== void 0 && t.determineType(data);
    if (actualType) {
      return actualType;
    }
    if (!t.determineType) {
      return getSubType(t._subtype);
    }
    const subTypes = t._types.map((t2) => getSubType(t2, data));
    const modelWithProperties = subTypes.find((x) => isModelType(x) || isReferenceType(x));
    if (modelWithProperties) {
      return getSubType(modelWithProperties, data);
    }
    return t;
  } else if (isLateType(t)) {
    return getSubType(t.getSubType(), data);
  } else if (isOptionalType(t)) {
    return getSubType(t._subtype, data);
  } else if (isArrayType2(t)) {
    return getSubType(t._subType, data);
  } else if (isReferenceType(t)) {
    return getSubType(t.targetType, data);
  } else {
    return t;
  }
}
function isArray(a) {
  return Array.isArray(a) || isObservableArray(a);
}
function isObject(a) {
  return a && typeof a === "object" && !isArray(a);
}
var useInsertionEffect2 = typeof window !== "undefined" ? (
  // useInsertionEffect is available in React 18+
  React.useInsertionEffect || React.useLayoutEffect
) : () => {
};
function useEvent(callback) {
  const latestRef = React.useRef(useEvent_shouldNotBeInvokedBeforeMount);
  useInsertionEffect2(() => {
    latestRef.current = callback;
  }, [callback]);
  const stableRef = React.useRef(null);
  if (!stableRef.current) {
    stableRef.current = function() {
      return latestRef.current.apply(this, arguments);
    };
  }
  return stableRef.current;
}
function useEvent_shouldNotBeInvokedBeforeMount() {
  throw new Error(
    "INVALID_USEEVENT_INVOCATION: the callback from useEvent cannot be invoked before the component has mounted."
  );
}

// src/merge.ts
function merge(data, typeDef, ctx, cloneInstances = false) {
  if (!data) {
    return data;
  }
  const instanceOrSnapshot = mergeInner(data, typeDef, ctx, cloneInstances);
  return instanceOrSnapshot;
}
function mergeInner(data, typeDef, ctx, cloneInstances = false) {
  if (!data || data instanceof Date || typeof data !== "object") {
    return data;
  }
  if (Array.isArray(data)) {
    return data.map((d) => mergeInner(d, getSubType(typeDef, d), ctx));
  }
  const snapshot = {};
  for (const key in data) {
    const realType = getRealTypeFromObject(typeDef, data, key);
    if (!realType) continue;
    snapshot[key] = mergeInner(data[key], realType, ctx);
  }
  if (isMapType(typeDef)) {
    return snapshot;
  }
  return mergeInstance(snapshot, data, typeDef, ctx, cloneInstances);
}
function mergeInstance(snapshot, data, typeDef, ctx, cloneInstances) {
  const modelType = getSubType(typeDef);
  const id = data[modelType.identifierAttribute];
  let instance = id && ctx.queryClient.queryStore.models.get(getKey(modelType, id));
  instance = cloneInstances && instance ? clone(instance) : instance;
  if (instance) {
    const root = getRoot(instance);
    unprotect(root);
    Object.assign(instance, mergeObjects(instance, snapshot, typeDef));
    protect(root);
    return instance;
  } else if (!instance) {
    instance = modelType.create(snapshot, ctx);
    const storedId = isStateTreeNode2(instance) ? getIdentifier2(instance) : id;
    if (storedId) {
      ctx.queryClient.rootStore.__MstQueryAction("put", modelType, storedId, instance);
      ctx.queryClient.queryStore.models.set(getKey(modelType, storedId), instance);
      return instance;
    }
  }
  return snapshot;
}
function mergeObjects(instance, data, typeDef) {
  const snapshot = {};
  const properties = data && isStateTreeNode2(data) ? getType2(data).properties : data;
  for (const key in properties) {
    const realType = getRealTypeFromObject(typeDef, data, key);
    if (!realType) continue;
    if (!isFrozenType2(realType) && isObject(data[key]) && !(data[key] instanceof Date) && isObject(instance[key]) && !getIdentifier2(instance[key])) {
      const mergedValue = mergeObjects(instance[key], data[key], realType);
      const isNode = isStateTreeNode2(data);
      isNode && unprotect(data);
      snapshot[key] = Object.assign(instance[key], mergedValue);
      isNode && protect(data);
    } else if (isStateTreeNode2(data[key]) && !getIdentifier2(data[key])) {
      snapshot[key] = getSnapshot(data[key]);
    } else {
      snapshot[key] = data[key];
    }
  }
  return snapshot;
}

// src/MstQueryHandler.ts
var EmptyRequest = Symbol("EmptyRequest");
var EmptyPagination = Symbol("EmptyPagination");
var DisposedError = class extends Error {
};
var QueryObserver = class {
  query;
  options;
  isQuery;
  isMounted = false;
  isFetchedAfterMount = false;
  constructor(query, isQuery) {
    this.query = query;
    this.isQuery = isQuery;
    makeObservable2(this, {
      isFetchedAfterMount: observable2
    });
  }
  subscribe() {
    if (this.query) {
      this.query.__MstQueryHandler.addQueryObserver(this);
    }
  }
  unsubscribe() {
    if (this.query) {
      this.query.__MstQueryHandler.removeQueryObserver(this);
    }
  }
  setOptions(options) {
    this.options = options;
    this.subscribe();
    if (this.isQuery) {
      options.isMounted = this.isMounted;
      const refetchRequestOnChanged = options.refetchOnChanged === "all" || options.refetchOnChanged === "request";
      options.isRequestEqual = true;
      if (options.enabled && refetchRequestOnChanged) {
        if (isStateTreeNode3(this.query.variables.request)) {
          const requestType = getType3(this.query.variables.request);
          options.isRequestEqual = equal(
            getSnapshot2(requestType.create(options.request)),
            getSnapshot2(this.query.variables.request)
          );
        } else {
          options.isRequestEqual = equal(options.request, this.query.variables.request);
        }
      }
      if (options.initialData && !options.isMounted) {
        const isStale = isDataStale(options.initialDataUpdatedAt, options.staleTime);
        if (!isStale) {
          this.query.__MstQueryHandler.hydrate(options);
        } else {
          this.query.__MstQueryHandler.queryWhenChanged(options);
        }
      } else {
        if (!options.isRequestEqual) {
          this.query.setData(null);
        }
        this.query.__MstQueryHandler.queryWhenChanged(options);
      }
    }
    if (!this.isMounted) {
      this.isMounted = true;
    }
  }
};
function subscribe(target, options) {
  const observer = new QueryObserver(target, false);
  observer.setOptions(options);
  observer.subscribe();
  addDisposer(target, () => {
    observer.unsubscribe();
  });
}
function onMutate(target, callback) {
  subscribe(target, {
    onMutate: (data, self) => {
      const root = getRoot2(self);
      unprotect2(root);
      callback(data, self);
      protect2(root);
    }
  });
}
var MstQueryHandler = class {
  isLoading = false;
  isRefetching = false;
  isFetchingMore = false;
  isFetched = false;
  error = null;
  queryObservers = [];
  options;
  previousVariables;
  model;
  type;
  queryClient;
  abortController;
  cachedAt;
  markedAsStale = false;
  isDisposed = false;
  constructor(model, options) {
    this.model = model;
    this.type = getType3(model);
    this.options = options ?? {};
    this.queryClient = getEnv(this.model).queryClient;
    this.model.$treenode.registerHook("afterCreate", () => this.onAfterCreate());
    this.model.$treenode.registerHook("beforeDestroy", () => this.onBeforeDestroy());
    makeObservable2(this, {
      isLoading: observable2,
      isRefetching: observable2,
      isFetchingMore: observable2,
      isFetched: observable2,
      error: observable2,
      hydrate: action2.bound,
      setData: action2.bound,
      setError: action2.bound,
      run: action2.bound,
      query: action2.bound,
      mutate: action2.bound,
      queryMore: action2.bound,
      refetch: action2.bound,
      abort: action2.bound,
      onAfterCreate: action2.bound,
      onBeforeDestroy: action2.bound
    });
  }
  run(options = {}) {
    const endpoint = this.options.endpoint ?? this.queryClient.config.queryOptions?.endpoint;
    if (!endpoint) {
      throw new Error("No query endpoint or global endpoint configured");
    }
    this.setVariables({ request: options.request, pagination: options.pagination });
    this.options.meta = options.meta;
    if (this.isLoading && this.abortController) {
      this.abortController.abort();
    }
    const abortController = new AbortController();
    this.abortController = abortController;
    this.isLoading = true;
    this.error = null;
    const opts = {
      ...options,
      request: this.model.variables.request,
      pagination: this.model.variables.pagination,
      meta: options.meta ?? {},
      signal: this.abortController.signal,
      setData: this.model.setData,
      query: this.model
    };
    return endpoint(opts).then((result) => {
      if (abortController?.signal.aborted || this.isDisposed) {
        throw new DisposedError();
      }
      return result;
    });
  }
  async queryWhenChanged(options) {
    if (this.isDisposed) {
      return;
    }
    if (!options.enabled) {
      return;
    }
    if (!options.isMounted) {
      const notInitialized = !this.isFetched && !this.isLoading && !this.cachedAt;
      if (notInitialized) {
        return this.model.query(options);
      }
      if (options.refetchOnMount === "never") {
        return;
      }
      if (options.refetchOnMount === "always") {
        return this.model.refetch(options);
      }
      if (!this.isStale(options)) {
        return;
      }
      return this.model.refetch(options);
    }
    if (!options.isRequestEqual) {
      return this.model.query(options);
    }
    const refetchPaginationOnChanged = options.refetchOnChanged === "all" || options.refetchOnChanged === "pagination";
    if (refetchPaginationOnChanged && options.pagination !== EmptyPagination && this.isFetched) {
      const isPaginationEqual = equal(options.pagination, this.model.variables.pagination);
      if (!isPaginationEqual) {
        return this.model.queryMore(options);
      }
    }
  }
  query(options = {}) {
    return this.run(options).then(
      (result) => this.onSuccess(result),
      (err) => this.onError(err)
    );
  }
  mutate(options = {}) {
    const { optimisticUpdate } = options;
    let updateRecorder;
    let result;
    if (optimisticUpdate) {
      updateRecorder = recordPatches(getRoot2(this.model));
      result = optimisticUpdate();
      updateRecorder.stop();
    }
    if (result) {
      const data = this.prepareData(result);
      this.notify({ onMutate: true }, data, this.model);
    }
    return this.run(options).then(
      (result2) => this.onSuccess(result2, { updateRecorder }),
      (err) => this.onError(err, { updateRecorder })
    );
  }
  queryMore(options = {}) {
    this.isFetchingMore = true;
    options.request = options.request ?? this.model.variables.request;
    options.pagination = options.pagination ?? this.model.variables.pagination;
    options.meta = options.meta ?? this.options.meta;
    return this.run(options).then(
      (result) => this.onSuccess(result, { shouldUpdate: false }),
      (err) => this.onError(err, { shouldUpdate: false })
    );
  }
  refetch(options = {}) {
    this.isRefetching = true;
    options.request = options.request ?? this.model.variables.request;
    options.pagination = options.pagination ?? this.model.variables.pagination;
    options.meta = options.meta ?? this.options.meta;
    return this.run(options).then(
      (result) => this.onSuccess(result),
      (err) => this.onError(err)
    );
  }
  invalidate() {
    this.markedAsStale = true;
    if (this.queryObservers.length > 0) {
      this.model.refetch();
    }
  }
  onSuccess(result, options = {}) {
    const { shouldUpdate = true, updateRecorder } = options;
    return () => {
      if (updateRecorder) {
        updateRecorder.undo();
      }
      if (this.isDisposed) {
        return { data: null, error: null, result: null };
      }
      if (this.markedAsStale) {
        this.markedAsStale = false;
      }
      let data;
      if (shouldUpdate) {
        data = this.setData(result);
      } else {
        data = this.prepareData(result);
      }
      this.cachedAt = /* @__PURE__ */ new Date();
      if (this.error) {
        this.error = null;
      }
      if (this.isLoading) {
        this.isLoading = false;
      }
      if (this.isRefetching) {
        this.isRefetching = false;
      }
      if (this.isFetchingMore) {
        this.isFetchingMore = false;
        this.options.onQueryMore?.({
          data,
          pagination: this.model.variables.pagination,
          request: this.model.variables.request,
          query: this.model
        });
      }
      if (!this.isFetched) {
        this.isFetched = true;
      }
      this.queryObservers.forEach((observer) => {
        observer.isFetchedAfterMount = true;
      });
      if (this.model.isMutation) {
        this.notify({ onMutate: true }, data, this.model);
      }
      return { data, error: null, result };
    };
  }
  onError(err, options = {}) {
    const { shouldUpdate = true, updateRecorder } = options;
    return () => {
      if (updateRecorder) {
        updateRecorder.undo();
      }
      if (this.isDisposed) {
        return { data: null, error: null, result: null };
      }
      if (err instanceof DisposedError) {
        return { data: null, error: null, result: null };
      }
      if (shouldUpdate) {
        this.setData(null);
      }
      this.error = err;
      if (this.isLoading) {
        this.isLoading = false;
      }
      if (this.isRefetching) {
        this.isRefetching = false;
      }
      if (this.isFetchingMore) {
        this.isFetchingMore = false;
      }
      return { data: null, error: err, result: null };
    };
  }
  addQueryObserver(queryObserver) {
    if (!this.queryObservers.includes(queryObserver)) {
      this.queryObservers.push(queryObserver);
    }
  }
  removeQueryObserver(queryObserver) {
    this.queryObservers = this.queryObservers.filter((observer) => observer !== queryObserver);
  }
  notify(notifyOptions, ...args) {
    for (let observer of this.queryObservers) {
      for (let key of Object.keys(notifyOptions)) {
        observer.options[key]?.(...args);
      }
    }
  }
  abort() {
    this.abortController?.abort();
    this.abortController = void 0;
    if (!this.isDisposed) {
      this.revertVariables();
    }
  }
  setError(error) {
    this.error = error;
  }
  setVariables(variables) {
    let request = variables.request ?? EmptyRequest;
    let pagination = variables.pagination ?? EmptyPagination;
    if (request === EmptyRequest && this.model.variables.request) {
      request = this.model.variables.request;
    }
    if (pagination === EmptyPagination && this.model.variables.pagination) {
      pagination = this.model.variables.pagination;
    }
    this.model.__MstQueryHandlerAction(() => {
      this.model.variables = { request, pagination };
    });
  }
  revertVariables() {
    this.model.__MstQueryHandlerAction(() => {
      this.model.variables = this.previousVariables;
    });
  }
  prepareData(data) {
    return merge(data, this.type.properties.data, this.queryClient.config.env, true);
  }
  setData(data) {
    this.model.__MstQueryHandlerAction(() => {
      if (isStateTreeNode3(data)) {
        if (isReferenceType2(this.type.properties.data)) {
          this.model.data = getIdentifier3(data);
        } else {
          this.model.data = getSnapshot2(data);
        }
      } else {
        this.model.data = merge(
          data,
          this.type.properties.data,
          this.queryClient.config.env
        );
      }
    });
    return this.model.data;
  }
  hydrate(options) {
    const { initialData, request, pagination } = options;
    this.setVariables({ request, pagination });
    this.options.meta = options.meta;
    this.isLoading = false;
    this.setData(initialData);
    this.cachedAt = /* @__PURE__ */ new Date();
  }
  isStale(options) {
    if (!this.cachedAt) {
      return false;
    }
    return this.markedAsStale || isDataStale(this.cachedAt.getTime(), options.staleTime);
  }
  onAfterCreate() {
    this.queryClient.queryStore.setQuery(this.model);
  }
  onBeforeDestroy() {
    this.queryClient.queryStore.removeQuery(this.model);
    this.isDisposed = true;
    this.abort();
  }
};
function isDataStale(cachedAt, staleTime = 0) {
  const now = Date.now();
  const cachedTime = cachedAt ?? now;
  return now - cachedTime >= staleTime;
}

// src/hooks.ts
import { useContext as useContext2, useEffect, useRef as useRef3, useState } from "react";

// src/create.ts
import { types, flow } from "mobx-state-tree";
function createQuery(name, options) {
  const {
    data = types.frozen(),
    request = types.frozen(),
    endpoint
  } = options;
  return types.model(name, {
    data: types.maybeNull(data),
    variables: types.optional(
      types.model({
        request: types.maybe(request)
      }),
      { request: void 0 }
    )
  }).volatile((self) => ({
    __MstQueryHandler: new MstQueryHandler(self, { endpoint }),
    isQuery: true,
    isInfinte: false,
    isMutation: false
  })).views((self) => ({
    get isLoading() {
      return self.__MstQueryHandler.isLoading;
    },
    get error() {
      return self.__MstQueryHandler.error;
    },
    get isRefetching() {
      return self.__MstQueryHandler.isRefetching;
    },
    get isFetched() {
      return self.__MstQueryHandler.isFetched;
    },
    get cachedAt() {
      return self.__MstQueryHandler.cachedAt;
    }
  })).actions((self) => ({
    __MstQueryHandlerAction(action3) {
      return action3();
    },
    query: flow(function* (options2) {
      const next = yield self.__MstQueryHandler.query(options2);
      return next();
    }),
    refetch: flow(function* (options2) {
      const next = yield self.__MstQueryHandler.refetch(options2);
      return next();
    }),
    invalidate() {
      self.__MstQueryHandler.invalidate();
    },
    setData(data2) {
      return self.__MstQueryHandler.setData(data2);
    },
    abort: self.__MstQueryHandler.abort
  }));
}
function createInfiniteQuery(name, options) {
  const {
    data = types.frozen(),
    request = types.frozen(),
    pagination = types.frozen(),
    endpoint,
    onQueryMore
  } = options;
  return types.model(name, {
    data: types.maybeNull(data),
    variables: types.optional(
      types.model({
        request: types.maybe(request),
        pagination: types.maybe(pagination)
      }),
      { request: void 0, pagination: void 0 }
    )
  }).volatile((self) => ({
    __MstQueryHandler: new MstQueryHandler(self, { endpoint, onQueryMore }),
    isQuery: true,
    isInfinite: true,
    isMutation: false
  })).views((self) => ({
    get isLoading() {
      return self.__MstQueryHandler.isLoading;
    },
    get error() {
      return self.__MstQueryHandler.error;
    },
    get isRefetching() {
      return self.__MstQueryHandler.isRefetching;
    },
    get isFetchingMore() {
      return self.__MstQueryHandler.isFetchingMore;
    },
    get isFetched() {
      return self.__MstQueryHandler.isFetched;
    },
    get cachedAt() {
      return self.__MstQueryHandler.cachedAt;
    }
  })).actions((self) => ({
    __MstQueryHandlerAction(action3) {
      return action3();
    },
    query: flow(function* (options2) {
      const next = yield self.__MstQueryHandler.query(options2);
      return next();
    }),
    queryMore: flow(function* (options2) {
      const next = yield self.__MstQueryHandler.queryMore(options2);
      return next();
    }),
    refetch: flow(function* (options2) {
      const next = yield self.__MstQueryHandler.refetch(options2);
      return next();
    }),
    invalidate() {
      self.__MstQueryHandler.invalidate();
    },
    setData(data2) {
      return self.__MstQueryHandler.setData(data2);
    },
    abort: self.__MstQueryHandler.abort
  }));
}
function createMutation(name, options = {}) {
  const {
    data = types.frozen(),
    request = types.frozen(),
    endpoint
  } = options;
  return types.model(name, {
    data: types.maybeNull(data),
    variables: types.optional(
      types.model({
        request: types.maybe(request)
      }),
      { request: void 0 }
    )
  }).volatile((self) => ({
    __MstQueryHandler: new MstQueryHandler(self, { endpoint }),
    isQuery: false,
    isInfinte: false,
    isMutation: true
  })).views((self) => ({
    get isLoading() {
      return self.__MstQueryHandler.isLoading;
    },
    get error() {
      return self.__MstQueryHandler.error;
    }
  })).actions((self) => ({
    __MstQueryHandlerAction(action3) {
      return action3();
    },
    mutate: flow(function* (options2) {
      const next = yield self.__MstQueryHandler.mutate(options2);
      return next();
    }),
    abort: self.__MstQueryHandler.abort
  }));
}
var VolatileQuery = createQuery("VolatileQuery", {});

// src/QueryClientProvider.tsx
import * as React2 from "react";
var Context = React2.createContext(void 0);
function createContext2(queryClient) {
  const QueryClientProvider = ({
    env,
    initialData,
    children
  }) => {
    const q = React2.useRef(null);
    if (!q.current) {
      q.current = queryClient.init(initialData, env);
    }
    return /* @__PURE__ */ React2.createElement(Context.Provider, { value: q.current }, children);
  };
  const useQueryClient = () => {
    const qc = React2.useContext(Context);
    if (!qc) {
      throw new Error("No QueryClient set, use QueryClientProvider to set one");
    }
    return qc;
  };
  const useRootStore = () => {
    const qc = React2.useContext(Context);
    return qc.rootStore;
  };
  return {
    queryClient,
    useQueryClient,
    useRootStore,
    QueryClientProvider
  };
}

// src/hooks.ts
function mergeWithDefaultOptions(key, options, queryClient) {
  return Object.assign({ queryClient }, queryClient.config[key], {
    enabled: true,
    ...options
  });
}
function useQuery(query, options = {}) {
  const [observer, setObserver] = useState(() => new QueryObserver(query, true));
  const queryClient = useContext2(Context);
  options = mergeWithDefaultOptions("queryOptions", options, queryClient);
  options.request = options.request ?? EmptyRequest;
  if (query.isInfinite) {
    throw new Error("useQuery should be used with a query that does not have pagination. Use useInfiniteQuery instead.");
  }
  useEffect(() => {
    if (observer.query !== query) {
      setObserver(new QueryObserver(query, true));
    }
  }, [query]);
  useEffect(() => {
    observer.setOptions(options);
    return () => {
      observer.unsubscribe();
    };
  }, [options]);
  return {
    data: query.data,
    dataUpdatedAt: query.__MstQueryHandler.cachedAt?.getTime(),
    error: query.error,
    isFetched: query.isFetched,
    isLoading: query.isLoading,
    isRefetching: query.isRefetching,
    query,
    refetch: query.refetch,
    isStale: query.__MstQueryHandler.isStale(options),
    isFetchedAfterMount: observer.isFetchedAfterMount
  };
}
function useInfiniteQuery(query, options = {}) {
  const [observer, setObserver] = useState(() => new QueryObserver(query, true));
  const queryClient = useContext2(Context);
  options = mergeWithDefaultOptions("queryOptions", options, queryClient);
  options.request = options.request ?? EmptyRequest;
  options.pagination = options.pagination ?? EmptyPagination;
  if (!query.isInfinite) {
    throw new Error("useInfiniteQuery should be used with a query that has pagination. Use useQuery instead.");
  }
  useEffect(() => {
    if (observer.query !== query) {
      setObserver(new QueryObserver(query, true));
    }
  }, [query]);
  useEffect(() => {
    observer.setOptions(options);
    return () => {
      observer.unsubscribe();
    };
  }, [options]);
  return {
    data: query.data,
    dataUpdatedAt: query.__MstQueryHandler.cachedAt?.getTime(),
    error: query.error,
    isFetched: query.isFetched,
    isLoading: query.isLoading,
    isRefetching: query.isRefetching,
    isFetchingMore: query.isFetchingMore,
    query,
    refetch: query.refetch,
    isStale: query.__MstQueryHandler.isStale(options),
    isFetchedAfterMount: observer.isFetchedAfterMount
  };
}
function useMutation(mutation, options = {}) {
  const [observer, setObserver] = useState(() => new QueryObserver(mutation, false));
  const queryClient = useContext2(Context);
  options = { queryClient, ...options };
  useEffect(() => {
    if (observer.query !== mutation) {
      setObserver(new QueryObserver(mutation, false));
    }
  }, [mutation]);
  useEffect(() => {
    observer.setOptions(options);
  }, [options]);
  const result = {
    data: mutation.data,
    error: mutation.error,
    isLoading: mutation.isLoading,
    mutation
  };
  const mutate = useEvent(
    (params) => {
      const result2 = mutation.mutate({ ...params, ...options });
      return result2;
    }
  );
  return [mutate, result];
}
function useRefQuery(query, queryClient) {
  const q = useRef3();
  if (!q.current) {
    q.current = query.create(void 0, queryClient.config.env);
  }
  return q.current;
}
function useVolatileQuery(options = {}) {
  const queryClient = useContext2(Context);
  const query = useRefQuery(VolatileQuery, queryClient);
  if (!query.__MstQueryHandler.options.endpoint) {
    query.__MstQueryHandler.options.endpoint = options.endpoint;
  }
  return useQuery(query, options);
}

// src/stores.ts
import {
  destroy,
  getEnv as getEnv2,
  getRoot as getRoot3,
  types as types2
} from "mobx-state-tree";
var createModelStore = (name, type) => {
  const modelStore = types2.model(name, {
    models: types2.map(type)
  }).actions((self) => {
    self.$treenode.registerHook("afterCreate", () => {
      const root = getRoot3(self);
      const modelStores = root.__getModelStores();
      if (!modelStores.get(type.name)) {
        modelStores.set(type.name, self);
      }
    });
    return {
      __MstQueryAction(action3, id, instance) {
        switch (action3) {
          case "get":
            return self.models.get(id);
          case "put":
            self.models.put(instance);
            break;
          case "delete":
            self.models.delete(id);
            break;
        }
      },
      merge(data) {
        return merge(data, type, getEnv2(self));
      }
    };
  });
  return modelStore;
};
var createRootStore = (props) => {
  return types2.model("RootStore", props).extend((self) => {
    const queryClient = getEnv2(self).queryClient;
    const modelStores = /* @__PURE__ */ new Map();
    self.$treenode.registerHook("afterCreate", () => {
      const log = console.log;
      console.log = () => {
      };
      for (let key in self) {
        console.log(self[key]);
      }
      console.log = log;
    });
    return {
      actions: {
        __MstQueryAction(action3, type, id, instance) {
          const store = modelStores.get(type.name);
          if (!store) {
            throw new Error(`Missing model store for type: ${type.name}`);
          }
          const result = store.__MstQueryAction(action3, id, instance);
          if (action3 === "delete") {
            destroy(instance);
          }
          return result;
        },
        runGc() {
          return queryClient.queryStore.runGc();
        }
      },
      views: {
        __getModelStores() {
          return modelStores;
        },
        getQueries(query, matcherFn) {
          return queryClient.queryStore.getQueries(query, matcherFn);
        }
      }
    };
  });
};

// src/QueryClient.ts
import { destroy as destroy2 } from "mobx-state-tree";
var defaultConfig = {
  env: {},
  queryOptions: {
    staleTime: 0,
    refetchOnMount: "if-stale",
    refetchOnChanged: "all"
  }
};
var QueryClient = class {
  config;
  rootStore;
  queryStore;
  #initialized = false;
  #initialData = {};
  constructor(config) {
    this.config = {
      ...defaultConfig,
      ...config,
      queryOptions: {
        ...defaultConfig.queryOptions,
        ...config.queryOptions
      }
    };
  }
  init(initialData = {}, env = {}) {
    if (this.#initialized) {
      return this;
    }
    this.config.env = env;
    this.config.env.queryClient = this;
    this.queryStore = new QueryStore(this);
    this.rootStore = this.config.RootStore.create(initialData, this.config.env);
    this.#initialized = true;
    return this;
  }
  reset() {
    destroy2(this.rootStore);
    this.queryStore = new QueryStore(this);
    this.rootStore = this.config.RootStore.create(this.#initialData, this.config.env);
  }
};
export {
  QueryClient,
  createContext2 as createContext,
  createInfiniteQuery,
  createModelStore,
  createMutation,
  createQuery,
  createRootStore,
  onMutate,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useVolatileQuery
};
//# sourceMappingURL=index.js.map